import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { deleteMultipleFromR2 } from '../../utils/r2Upload';

// 실제 데이터베이스 스키마에 맞는 인터페이스
interface Content {
  id: number;
  creator_id: string;
  video_url: string;
  thumbnail_url: string;
  thumbnail_second: number;
  description: string;
  keywords: string[];
  view_count: number;
  like_count: number;
  comment_count: number;
  short_code: string;
  status?: string; // status 필드 추가 (선택적)
  content_type: 'video' | 'image'; // 컨텐츠 타입 추가
  image_urls?: string[]; // 이미지 URL 배열 추가
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

interface ContentProductTag {
  id: number;
  contents_id: number;
  product_id: number;
  start_time: number;
  end_time: number;
  position_x: number;
  position_y: number;
  created_at: string;
  updated_at: string;
}

interface Product {
  product_id: number;
  product_name: string;
  price: number;
  status: string;
  main_images: string[];
  store_name: string;
  description: string;
  keywords: string[];
  created_at: string;
  updated_at: string;
}

const statusLabel = (status: string) => {
  if (status === 'active')
    return <Chip label="Active" color="primary" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
  if (status === 'inactive')
    return <Chip label="Inactive" color="default" size="small" sx={{ fontWeight: 700, borderRadius: 1, bgcolor: '#e0e0e0' }} />;
  return <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 700, borderRadius: 1 }} />;
};

// 확장 가능한 행 컴포넌트
function ExpandableRow({ content, products, onDelete, onEdit }: {
  content: Content;
  products: Product[];
  onDelete: (contentId: number) => void;
  onEdit: (contentId: number) => void;
}) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell sx={styles.table.cell}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ color: styles.text.primary }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={styles.table.cell}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={content.content_type === 'image' ? (content.image_urls?.[0] || content.thumbnail_url) : content.thumbnail_url || ''}
                alt={content.description}
                sx={{ width: 50, height: 50 }}
              />
              {/* 컨텐츠 타입 표시 */}
              <Chip
                label={content.content_type === 'image' ? 'IMAGE' : 'VIDEO'}
                color={content.content_type === 'image' ? 'secondary' : 'primary'}
                size="small"
                sx={{ 
                  position: 'absolute',
                  bottom: -8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '9px',
                  height: 16,
                  fontWeight: 700
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} color={styles.text.primary}>
                {content.description || t('seller.contents.noTitle')}
              </Typography>
              <Typography variant="caption" color={styles.text.secondary}>
                {content.keywords?.join(', ') || t('seller.contents.noKeywords')}
              </Typography>
              {/* 이미지 개수 표시 */}
              {content.content_type === 'image' && content.image_urls && (
                <Typography variant="caption" color={styles.text.secondary} sx={{ display: 'block' }}>
                  {content.image_urls.length} images
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={styles.table.cell}>
          <IconButton size="small" onClick={() => onEdit(content.id)} sx={{ color: styles.text.primary }}>
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(content.id)} sx={{ color: styles.text.primary }}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, ...styles.table.cell }} colSpan={3}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div" color={styles.text.primary}>
                {t('seller.contents.linkedProducts')}
              </Typography>
              {products.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={styles.table.header}>
                      <TableCell sx={styles.table.cell}>{t('orders.product')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('common.price')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('common.status')}</TableCell>
                      <TableCell sx={styles.table.cell}>{t('common.actions') || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.product_id}>
                        <TableCell sx={styles.table.cell}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={product.main_images?.[0] || ''}
                              alt={product.product_name}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight={600} color={styles.text.primary}>
                                {product.product_name}
                              </Typography>
                              <Typography variant="caption" color={styles.text.secondary}>
                                {product.keywords?.join(', ') || t('seller.contents.noKeywords')}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.table.cell}>
                          <Typography color={styles.text.primary}>{product.price.toLocaleString()}원</Typography>
                        </TableCell>
                        <TableCell sx={styles.table.cell}>{statusLabel(product.status)}</TableCell>
                        <TableCell sx={styles.table.cell}>
                          <IconButton size="small" sx={{ color: styles.text.primary }}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color={styles.text.secondary} sx={{ py: 2, textAlign: 'center' }}>
                  {t('seller.contents.noLinkedProducts')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
export default function SellerContents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<string>('seller');
  const isCreator = userRole === 'creator';
  const [contents, setContents] = useState<Content[]>([]);
  const [contentProducts, setContentProducts] = useState<{ [key: number]: Product[] }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // 삭제 확인 팝업 상태 추가
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<number | null>(null);

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // role 조회
  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('role').eq('id', user.id).single().then(({ data }) => {
      if (data?.role) setUserRole(data.role);
    });
  }, [user]);

  // 콘텐츠 데이터 로드
  useEffect(() => {
    const fetchContents = async () => {
      try {
        setLoading(true);

        if (!user) {
          console.error('사용자 정보를 가져올 수 없습니다.');
          setLoading(false);
          return;
        }

        // seller_profile에서 seller 정보 가져오기
        const { data: sellerProfile, error: profileError } = await supabase
          .from('seller_profile')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (profileError || !sellerProfile) {
          console.error('Seller Profile 정보를 찾을 수 없습니다:', profileError);
          return;
        }

        console.log('Seller Profile ID:', sellerProfile.id);

        // 해당 크리에이터의 콘텐츠들 가져오기 (seller_profile.id 사용)
        const { data: contentsData, error: contentsError } = await supabase
          .from('contents')
          .select('*, content_type, image_urls') // 이미지 관련 필드 추가
          .eq('creator_id', sellerProfile.id) // ✅ seller_profile.id 사용
          .eq('is_public', true);

        if (contentsError) {
          console.error('콘텐츠 데이터 로드 실패:', contentsError);
          return;
        }

        if (contentsData) {
          setContents(contentsData);
          
          // 각 콘텐츠의 연결된 상품 데이터 가져오기
          const productsMap: { [key: number]: Product[] } = {};
          
          for (const content of contentsData) {
            // content_product_tags에서 해당 콘텐츠의 상품 태그들 가져오기
            const { data: tagsData, error: tagsError } = await supabase
              .from('content_product_tags')
              .select('*')
              .eq('contents_id', content.id);

            if (!tagsError && tagsData && tagsData.length > 0) {
              // 상품 ID들 추출
              const productIds = tagsData.map(tag => tag.product_id);
              
              // products 테이블에서 해당 상품들 가져오기
              const { data: productsData, error: productsError } = await supabase
                .from('products')
                .select('*')
                .in('product_id', productIds);

              if (!productsError && productsData) {
                productsMap[content.id] = productsData;
              }
            } else {
              productsMap[content.id] = [];
            }
          }
          
          setContentProducts(productsMap);
        }
      } catch (error) {
        console.error('데이터 로드 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContents();
  }, [user]);

  const handleAddContent = async () => {
    try {
      if (!user) {
        alert('사용자 정보를 가져올 수 없습니다.');
        return;
      }

      // 크리에이터는 상점/상품 체크 없이 바로 이동
      if (isCreator) {
        window.location.href = '/creator/addcontents';
        return;
      }

      // seller_profile에서 seller 정보 가져오기
      const { data: sellerProfile, error: profileError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !sellerProfile) {
        alert('Seller 정보를 찾을 수 없습니다.');
        return;
      }

      // seller_store 테이블에서 해당 seller_id가 존재하는지 확인
      const { data: storeData, error: storeError } = await supabase
        .from('seller_store')
        .select('id')
        .eq('seller_id', sellerProfile.id)
        .single();

      if (storeError || !storeData) {
        // 상점이 등록되지 않은 경우
        alert('상점을 먼저 등록한 후 상품을 등록해주세요.');
        navigate('/seller/shop');
        return;
      }

      // 상점이 존재하면 상품 등록 페이지로 이동
      window.location.href = '/seller/addcontents';

    } catch (error) {
      console.error('상점 확인 중 오류:', error);
      alert('상점 정보를 확인하는 중 오류가 발생했습니다.');
    }
    window.location.href = '/seller/addcontents';
  };

  // 삭제 확인 함수
  const handleDeleteConfirm = (contentId: number) => {
    setContentToDelete(contentId);
    setDeleteDialogOpen(true);
  };

  // 실제 삭제 실행 함수 (Soft Delete)
  const handleDeleteContent = async (contentId: number) => {
    try {
      const target = contents.find(c => c.id === contentId);
      if (!target) {
        console.error('삭제할 컨텐츠를 찾을 수 없습니다.');
        return;
      }

      // 1. affiliate_earnings 존재 여부 확인 (수익 데이터 보호)
      const { data: earnings } = await supabase
        .from('affiliate_earnings')
        .select('id')
        .eq('content_id', contentId)
        .limit(1);

      const hasEarnings = earnings && earnings.length > 0;

      // 2. Soft Delete: is_public = false 처리
      const { error: softDeleteError } = await supabase
        .from('contents')
        .update({ is_public: false })
        .eq('id', contentId);

      if (softDeleteError) {
        console.error('콘텐츠 비공개 처리 실패:', softDeleteError);
        return;
      }

      if (hasEarnings) {
        // 수익 데이터 있음 → R2 파일 유지 (DB soft delete만)
        console.log('✅ 수익 데이터 존재 — R2 파일 유지, DB만 비공개 처리');
      } else {
        // 수익 데이터 없음 → product_tags 삭제 + R2 파일 삭제
        const { error: tagsError } = await supabase
          .from('content_product_tags')
          .delete()
          .eq('contents_id', contentId);

        if (tagsError) {
          console.error('상품 태그 삭제 실패:', tagsError);
        }

        const fileUrlsToDelete: string[] = [];
        if (target.video_url) fileUrlsToDelete.push(target.video_url);
        if (target.thumbnail_url) fileUrlsToDelete.push(target.thumbnail_url);
        if (target.image_urls?.length) fileUrlsToDelete.push(...target.image_urls);

        if (fileUrlsToDelete.length > 0) {
          console.log(`🗑️ ${fileUrlsToDelete.length}개 파일 R2에서 삭제 시작`);
          await deleteMultipleFromR2(fileUrlsToDelete);
          console.log('✅ R2 파일 삭제 완료');
        }
      }

      // 3. 로컬 상태 업데이트 (목록에서 제거)
      setContents(prev => prev.filter(c => c.id !== contentId));
      const newProducts = { ...contentProducts };
      delete newProducts[contentId];
      setContentProducts(newProducts);

      // 4. 팝업 닫기
      setDeleteDialogOpen(false);
      setContentToDelete(null);

      console.log(`✅ 콘텐츠 soft delete 완료 (earnings: ${hasEarnings})`);

    } catch (error) {
      console.error('❌ 삭제 중 오류:', error);
    }
  };

  const handleSaveContent = async (contentData: Partial<Content>) => {
    if (!user) {
      console.error('사용자 정보를 가져올 수 없습니다.');
      return;
    }

    try {

      // 새로운 콘텐츠 생성
      const { data: newContent, error: insertError } = await supabase
        .from('contents')
        .insert({
          creator_id: user.id,
          ...contentData
        })
        .select()
        .single();

      if (insertError) {
        console.error('콘텐츠 생성 실패:', insertError);
        return;
      }

      // 로컬 상태 업데이트
      setContents(prev => [...prev, newContent]);
      setOpenDialog(false);
    } catch (error) {
      console.error('콘텐츠 저장 실패:', error);
    }
  };

  const filteredContents = contents.filter(content => {
    const matchesSearch = (content.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (content.keywords?.join(' ') || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      tab === 0 ||
      (tab === 1 && content.status === 'active') ||
      (tab === 2 && content.status === 'inactive') ||
      (tab === 3 && content.status === 'pending');
    return matchesSearch && matchesStatus;
  });

  // 페이지네이션 적용
  const paginatedContents = filteredContents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // 페이지네이션 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', bgcolor: 'transparent' }}>
        <Typography color={styles.text.primary}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageContainer}>
      <Typography variant="h4" fontWeight={700} mb={3} sx={styles.title}>
        {t('seller.contents.title')}
      </Typography>
      <Paper sx={{ ...styles.paper, p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: styles.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...styles.input, width: 320 }}
            size="small"
          />
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ 
              minHeight: 40, 
              ml: { sm: 2 },
              '& .MuiTab-root': {
                color: styles.text.secondary,
                '&.Mui-selected': {
                  color: styles.text.primary
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: styles.text.primary
              }
            }}
            TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
          >
            <Tab label={t('common.all') || 'All'} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('status.active')} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('status.inactive')} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('status.pending')} sx={{ fontWeight: 700, minHeight: 40 }} />
          </Tabs>
          <Box flex={1} />
          <Button variant="contained" startIcon={<Add />} sx={{ ...styles.button.primary, fontWeight: 700, minWidth: 140 }} onClick={handleAddContent}>
            {t('seller.contents.newContent')}
          </Button>
        </Stack>
      </Paper>
      <TableContainer component={Paper} sx={styles.paper}>
        <Table>
          <TableHead>
            <TableRow sx={styles.table.header}>
              <TableCell sx={styles.table.cell}></TableCell>
              <TableCell sx={styles.table.cell}>{t('seller.contents.title')}</TableCell>
              <TableCell sx={styles.table.cell}>{t('common.actions') || 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedContents.map((content) => (
              <ExpandableRow
                key={content.id}
                content={content}
                products={contentProducts[content.id] || []}
                onDelete={handleDeleteConfirm}
                onEdit={(contentId) => navigate(isCreator ? `/creator/addcontents/${contentId}` : `/seller/addcontents/${contentId}`)}
              />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredContents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('sellerProducts.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </TableContainer>
      
      {/* 콘텐츠 편집 다이얼로그 */}
      <ContentDialog
        open={openDialog}
        content={editingContent}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveContent}
      />
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => contentToDelete && handleDeleteContent(contentToDelete)}
      />
    </Box>
  );
}

interface ContentDialogProps {
  open: boolean;
  content: Content | null;
  onClose: () => void;
  onSave: (contentData: Partial<Content>) => void;
}

function ContentDialog({ open, content, onClose, onSave }: ContentDialogProps) {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [formData, setFormData] = useState({
    description: '',
    keywords: [] as string[],
  });

  useEffect(() => {
    if (content) {
      setFormData({
        description: content.description || '',
        keywords: content.keywords || [],
      });
    } else {
      setFormData({
        description: '',
        keywords: [],
      });
    }
  }, [content]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ color: styles.text.primary }}>
        {t('seller.contents.newContent')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              minRows={3}
              sx={styles.input}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Keywords (comma separated)"
              value={formData.keywords.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
              })}
              placeholder={t('seller.contents.keywordsPlaceholder')}
              sx={styles.input}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: styles.text.secondary }}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" sx={styles.button.primary}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 삭제 확인 팝업 컴포넌트
function DeleteConfirmDialog({ open, onClose, onConfirm }: { 
  open: boolean; 
  onClose: () => void; 
  onConfirm: () => void; 
}) {
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: styles.paper }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: styles.text.primary }}>
        콘텐츠 삭제
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mb: 2, color: styles.text.primary }}>
          콘텐츠를 비공개 처리하시겠습니까?
        </Typography>
        <Typography sx={{ textAlign: 'center', color: styles.text.secondary }}>
          앱 피드에서 즉시 숨겨집니다. 수익 데이터가 있는 경우 파일은 보존됩니다.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ 
          minWidth: 100,
          borderColor: styles.table.cell.borderColor,
          color: styles.text.primary,
          '&:hover': {
            borderColor: styles.text.primary,
            bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
          }
        }}>
          취소
        </Button>
        <Button onClick={onConfirm} variant="contained" sx={{ 
          minWidth: 100, 
          bgcolor: '#f44336',
          color: '#fff',
          '&:hover': {
            bgcolor: '#d32f2f'
          }
        }}>
          삭제
        </Button>
      </DialogActions>
    </Dialog>
  );
} 